import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { gql } from '@apollo/client';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { first, map, shareReplay } from 'rxjs/operators';
import { CurrencyCode, Money } from '../model/models';

export interface BuyerAgingSummaryGql {
    invoiceCount: number;
    openTotalAmount: Money;
    openAmountCurrent: Money;
    openAmountDayOneToThirty: Money;
    openAmountDayThirtyOneToSixty: Money;
    openAmountDaySixtyOnePlus: Money;
}

@Injectable({ providedIn: 'root' })
export class BuyerAgingSummaryDataSourceFactory {
    constructor(private _http: HttpClient, @Inject(SETTINGS) private _settings: ISettings) {}

    public createBuyerAgingSummaryDataSource(
        buyerKeys: string[] = [],
        payLaterOnly = false
    ): LsqDataSource<BuyerAgingSummaryGql> {
        const data$ = new BehaviorSubject<BuyerAgingSummaryGql>({
            invoiceCount: 0,
            openTotalAmount: {
                cents: 0,
                currencyCode: CurrencyCode.USD
            },
            openAmountCurrent: {
                cents: 0,
                currencyCode: CurrencyCode.USD
            },
            openAmountDayOneToThirty: {
                cents: 0,
                currencyCode: CurrencyCode.USD
            },
            openAmountDayThirtyOneToSixty: {
                cents: 0,
                currencyCode: CurrencyCode.USD
            },
            openAmountDaySixtyOnePlus: {
                cents: 0,
                currencyCode: CurrencyCode.USD
            }
        });
        const error$ = new Subject<unknown>();
        const loading$ = new BehaviorSubject<boolean>(true);
        const noData$ = combineLatest([data$, loading$]).pipe(
            map(([data, loading]): boolean => !loading && !data),
            shareReplay(1)
        );
        this._http
            .post<{ data: { buyerAgingSummary: BuyerAgingSummaryGql }; errors?: any[] }>(
                this._settings.httpApiServiceNameUrlMap.financingGql + '/v1/query',
                {
                    query: gql`
                        query GetBuyerAgingSummary($buyerIds: [ID!]!, $payLaterOnly: Boolean!) {
                            buyerAgingSummary(
                                input: {
                                    companyFilter: { buyerIds: $buyerIds }
                                    payLaterOnly: $payLaterOnly
                                }
                            ) {
                                invoiceCount
                                openTotalAmount {
                                    cents
                                    currencyCode
                                }
                                openAmountCurrent {
                                    cents
                                    currencyCode
                                }
                                openAmountDayOneToThirty {
                                    cents
                                    currencyCode
                                }
                                openAmountDayThirtyOneToSixty {
                                    cents
                                    currencyCode
                                }
                                openAmountDaySixtyOnePlus {
                                    cents
                                    currencyCode
                                }
                            }
                        }
                    `.loc.source.body,
                    variables: {
                        buyerIds: buyerKeys,
                        payLaterOnly: payLaterOnly
                    }
                }
            )
            .pipe(first())
            .subscribe(
                (respBody) => {
                    if (respBody.errors) {
                        error$.next(respBody.errors);
                    } else {
                        data$.next(respBody.data.buyerAgingSummary);
                    }
                },
                (error) => error$.next(error),
                () => loading$.next(false)
            );
        return new LsqDataSource<BuyerAgingSummaryGql>({
            data$,
            noData$,
            loading$,
            error$
        });
    }
}
